/**
 * Frontend JavaScript for the Carbon Footprint Report block.
 * Handles form submission using cloudavocado-forms plugin.
 */

/* global cloudavocado_forms, grecaptcha */
/* eslint-disable camelcase, no-undef */

'use strict';

/**
 * Show error message.
 * @param {HTMLElement} formContainer
 * @param {string}      message
 * @param {HTMLElement} submitButton
 */
function showError( formContainer, message, submitButton = null ) {
	const errorElement = formContainer.querySelector(
		'.carbon-footprint-report-error-message'
	);
	const placeholder = formContainer.querySelector(
		'.carbon-footprint-report-message-placeholder'
	);
	if ( errorElement ) {
		errorElement.textContent = message;
		errorElement.classList.remove( 'd-none' );
		errorElement.classList.add( 'd-block' );
	}

	// Hide placeholder when error is shown.
	if ( placeholder ) {
		placeholder.style.display = 'none';
	}

	// Add error class to form container.
	formContainer.classList.add( 'error' );
	formContainer.classList.remove( 'success' );

	// Disable button if provided.
	if ( submitButton ) {
		submitButton.disabled = true;
	}
}

/**
 * Hide error message.
 * @param {HTMLElement} formContainer
 * @param {HTMLElement} submitButton
 */
function hideError( formContainer, submitButton = null ) {
	const errorElement = formContainer.querySelector(
		'.carbon-footprint-report-error-message'
	);
	const placeholder = formContainer.querySelector(
		'.carbon-footprint-report-message-placeholder'
	);
	if ( errorElement ) {
		errorElement.textContent = '';
		errorElement.classList.add( 'd-none' );
		errorElement.classList.remove( 'd-block' );
	}

	// Show placeholder if no messages are visible.
	const successElement = formContainer.querySelector(
		'.carbon-footprint-report-success-message'
	);
	const isSuccessVisible =
		successElement && ! successElement.classList.contains( 'd-none' );
	if ( placeholder && ! isSuccessVisible ) {
		placeholder.style.display = 'block';
	}

	// Remove error class from form container.
	formContainer.classList.remove( 'error' );

	// Enable button if provided.
	if ( submitButton ) {
		submitButton.disabled = false;
	}
}

/**
 * Show success message.
 * @param {HTMLElement} formContainer
 * @param {string}      message
 */
function showSuccess( formContainer, message ) {
	const successElement = formContainer.querySelector(
		'.carbon-footprint-report-success-message'
	);
	const placeholder = formContainer.querySelector(
		'.carbon-footprint-report-message-placeholder'
	);
	if ( successElement ) {
		successElement.textContent = message;
		successElement.classList.remove( 'd-none' );
		successElement.classList.add( 'd-block' );
	}

	// Hide placeholder when success is shown.
	if ( placeholder ) {
		placeholder.style.display = 'none';
	}

	// Add success class to form container.
	formContainer.classList.add( 'success' );
	formContainer.classList.remove( 'error' );
}

/**
 * Hide success message.
 * @param {HTMLElement} formContainer
 */
function hideSuccess( formContainer ) {
	const successElement = formContainer.querySelector(
		'.carbon-footprint-report-success-message'
	);
	const placeholder = formContainer.querySelector(
		'.carbon-footprint-report-message-placeholder'
	);
	if ( successElement ) {
		successElement.textContent = '';
		successElement.classList.add( 'd-none' );
		successElement.classList.remove( 'd-block' );
	}

	// Show placeholder if no messages are visible.
	const errorElement = formContainer.querySelector(
		'.carbon-footprint-report-error-message'
	);
	const isErrorVisible =
		errorElement && ! errorElement.classList.contains( 'd-none' );
	if ( placeholder && ! isErrorVisible ) {
		placeholder.style.display = 'block';
	}

	// Remove success class from form container.
	formContainer.classList.remove( 'success' );
}

/**
 * Get error message from error code.
 * @param {string} errorCode
 * @return {string} Error message string
 */
function getErrorMessage( errorCode ) {
	const errorMessages = {
		error_invalid_email: 'Please enter a valid email address.',
		error_required: 'Please enter your email address.',
		error_min_length:
			'Email address is too short. Please enter a valid email.',
		error_max_length:
			'Email address is too long. Please use a shorter email.',
	};

	return (
		errorMessages[ errorCode ] ||
		'Something went wrong. Please try again later.'
	);
}

/**
 * Validate email input.
 * @param {string} email
 * @return {Object} { isValid: boolean, errorMessage: string|null }
 */
function validateEmail( email ) {
	const trimmedEmail = email.trim();

	// Check if empty.
	if ( ! trimmedEmail ) {
		return {
			isValid: false,
			errorMessage: 'Please enter your email address.',
		};
	}

	// Email format validation.
	const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
	if ( ! emailRegex.test( trimmedEmail ) ) {
		return {
			isValid: false,
			errorMessage: 'Please enter a valid email address.',
		};
	}

	// Length validation (max 254 characters as per plugin).
	if ( trimmedEmail.length > 254 ) {
		return {
			isValid: false,
			errorMessage:
				'Email address is too long. Please use a shorter email.',
		};
	}

	return {
		isValid: true,
		errorMessage: null,
	};
}

/**
 * Handle input change - real-time validation.
 * @param {Event} event
 */
function handleInputChange( event ) {
	const emailInput = event.target;
	const form = emailInput.closest( 'form' );
	const formContainer = form
		? form.closest( '.carbon-footprint-report-form' )
		: emailInput.closest( '.carbon-footprint-report-form' );

	if ( ! formContainer ) {
		return;
	}

	// Check if form has been submitted (has data attribute).
	const formElement =
		form || emailInput.closest( '.carbon-footprint-report-form-inner' );

	const email = emailInput.value;
	const validation = validateEmail( email );

	// Get submit button.
	const submitButton = formElement
		? formElement.querySelector( 'button[type="submit"]' )
		: null;

	if ( validation.isValid ) {
		// Hide both messages when form is valid.
		hideError( formContainer, submitButton );
		hideSuccess( formContainer );
	} else {
		// Show error and disable button.
		showError( formContainer, validation.errorMessage, submitButton );
		hideSuccess( formContainer );
	}
}

/**
 * Handle form submission.
 * @param {Event} event
 */
function handleFormSubmit( event ) {
	event.preventDefault();

	const form = event.target;
	const formContainer = form.closest( '.carbon-footprint-report-form' );
	const emailInput = form.querySelector( 'input[name="email"]' );
	const submitButton = form.querySelector( 'button[type="submit"]' );
	const email = emailInput.value;

	// Mark form as validated so real-time validation can work.
	form.setAttribute( 'data-validated', 'true' );

	// Validate email.
	const validation = validateEmail( email );

	if ( ! validation.isValid ) {
		showError( formContainer, validation.errorMessage, submitButton );
		hideSuccess( formContainer );
		emailInput.focus();
		return;
	}

	// Hide any errors if validation passes.
	hideError( formContainer, submitButton );
	hideSuccess( formContainer );

	// Disable form during submission.
	emailInput.disabled = true;
	submitButton.disabled = true;
	const originalButtonText = submitButton.textContent;
	submitButton.textContent = 'Submitting...';

	// Check if cloudavocado_forms is available.
	if ( typeof cloudavocado_forms === 'undefined' ) {
		showError(
			formContainer,
			'Form submission is not available. Please refresh the page.',
			submitButton
		);
		emailInput.disabled = false;
		submitButton.disabled = false;
		submitButton.textContent = originalButtonText;
		return;
	}

	// Prepare form data.
	const formData = {
		action: 'cloudavocado_forms_submit_contact_form',
		nonce: cloudavocado_forms.nonce,
		first_name: 'N/A',
		last_name: 'N/A',
		email,
		message: 'I am interested in carbon footprint report',
	};

	// Handle reCAPTCHA if enabled.
	const handleSubmission = ( recaptchaToken = '' ) => {
		if ( recaptchaToken ) {
			formData.recaptcha_token = recaptchaToken;
		}

		// Submit form.
		fetch( cloudavocado_forms.ajax_url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: new URLSearchParams( formData ),
		} )
			.then( ( response ) => response.json() )
			.then( ( data ) => {
				if ( data.success ) {
					// Success - clear input and show success message.
					hideError( formContainer, submitButton );
					emailInput.value = '';
					emailInput.disabled = false;
					submitButton.disabled = false;
					submitButton.textContent = originalButtonText;
					showSuccess(
						formContainer,
						'Thank you! We will send you the carbon footprint report shortly.'
					);
				} else {
					// Error - show error message.
					const errorCode = data.data?.code || 'error_unknown';
					const errorMessage = getErrorMessage( errorCode );
					hideSuccess( formContainer );
					showError( formContainer, errorMessage, submitButton );
					emailInput.disabled = false;
					submitButton.disabled = false;
					submitButton.textContent = originalButtonText;
					emailInput.focus();
				}
			} )
			.catch( () => {
				hideSuccess( formContainer );
				showError(
					formContainer,
					'Something went wrong. Please try again later.',
					submitButton
				);
				emailInput.disabled = false;
				submitButton.disabled = false;
				submitButton.textContent = originalButtonText;
				emailInput.focus();
			} );
	};

	// Check if reCAPTCHA is enabled.
	if (
		cloudavocado_forms.recaptcha_site_key &&
		typeof grecaptcha !== 'undefined'
	) {
		grecaptcha.ready( function () {
			grecaptcha
				.execute( cloudavocado_forms.recaptcha_site_key, {
					action: 'submit',
				} )
				.then( function ( token ) {
					handleSubmission( token );
				} )
				.catch( function () {
					// Show error and re-enable form.
					showError(
						formContainer,
						'Something went wrong. Please try again later.',
						submitButton
					);
					emailInput.disabled = false;
					submitButton.disabled = false;
					submitButton.textContent = originalButtonText;
				} );
		} );
	} else {
		// No reCAPTCHA, submit directly.
		handleSubmission();
	}
}

/**
 * Initialize all carbon footprint report forms on the page.
 */
function initCarbonFootprintReportForms() {
	const forms = document.querySelectorAll(
		'.carbon-footprint-report-form-inner[data-form-type="carbon-footprint-report"]'
	);
	forms.forEach( ( form ) => {
		form.addEventListener( 'submit', handleFormSubmit );

		// Add real-time validation on input change.
		const emailInput = form.querySelector( 'input[name="email"]' );
		if ( emailInput ) {
			emailInput.addEventListener( 'input', handleInputChange );
		}
	} );
}

// Initialize when DOM is ready.
if ( document.readyState === 'loading' ) {
	document.addEventListener(
		'DOMContentLoaded',
		initCarbonFootprintReportForms
	);
} else {
	initCarbonFootprintReportForms();
}
